import type { EventSettings } from '@/models/eventModel';

/** The event fields the rule reads: the switch and the two dates that end it. */
export type RegistrantVisibilityEvent = {
  settings: unknown;
  registration_closes: string | null;
  start_date: string | null;
};

/**
 * Registration has closed: its close date has passed, or, for an event without one, the event has
 * started. Entries are final from then on, so there is nothing left to keep private.
 */
export function registrationHasClosed(
  event: Pick<RegistrantVisibilityEvent, 'registration_closes' | 'start_date'>,
  now: Date = new Date(),
): boolean {
  const closes = event.registration_closes ?? event.start_date;
  return closes != null && now.getTime() >= new Date(closes).getTime();
}

/**
 * The organiser's "hide registrants from public" switch, read one way: on, and only while
 * registration is open (product ruling 2026-10-13). Once registration closes everyone sees the
 * entrants. SQL twin: `public.registrants_hidden_for_event` (migration 20261013120000); a parity
 * test pins the two to one table of cases.
 *
 * This is the first half of the visibility rule. The whole rule is
 * `getCanSeeRegistrants` (src/models/eventModel.ts): not hidden, or the viewer
 * holds an entry. That function calls this one, so the two can never drift.
 *
 * Server-only: the answer depends on the clock, so compute it on the server and hand the
 * browser the boolean (a browser clock may disagree). When it returns `true`, only the server
 * can answer for a given viewer — ask `/api/event/{id}/registrants-visibility`.
 */
export function hidesRegistrantsFromPublic(
  event: RegistrantVisibilityEvent,
  now: Date = new Date(),
): boolean {
  const settings = (event.settings ?? {}) as EventSettings;
  return !!settings.hide_registrants_from_public && !registrationHasClosed(event, now);
}
